import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { QuickAmounts } from "@/components/QuickAmounts";
import { storage } from "@/lib/storage";
import { getCurrency, formatMoney } from "@/lib/currencies";
import { Check, Banknote, Smartphone, Wallet } from "lucide-react";

const SOURCES = [
  { id: "cash", label: "Cash", icon: Banknote },
  { id: "upi", label: "UPI", icon: Smartphone },
  { id: "wallet", label: "Wallet", icon: Wallet },
];

export default function ReceiveCash() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("cash");
  const [ref, setRef] = useState("");
  const [currency, setCurrency] = useState(storage.getCurrency());
  const [done, setDone] = useState(false);

  const cur = getCurrency(currency);
  const valid = Number(amount) > 0;

  const submit = () => {
    storage.addTransaction({
      direction: "received",
      amount: Number(amount),
      currency,
      method: source,
      note: ref,
    });
    setDone(true);
    setTimeout(() => navigate("/merchant"), 1200);
  };

  return (
    <div className="page-shell" data-testid="receive-cash-page">
      <Header title="Cash / UPI Record" subtitle="Log any manual payment" />

      <div className="card-glass gold-glow rounded-2xl p-6 fade-slide-up">
        <div className="flex items-center justify-between mb-4">
          <span className="stat-label">Payment source</span>
          <CurrencyPicker value={currency} onChange={setCurrency} compact />
        </div>

        <div className="grid grid-cols-3 gap-2 mb-6">
          {SOURCES.map((s) => (
            <button
              key={s.id}
              onClick={() => setSource(s.id)}
              className={`py-3 rounded-xl border flex flex-col items-center gap-1.5 transition ${
                source === s.id
                  ? "border-[rgba(230,180,85,0.6)] bg-[rgba(230,180,85,0.1)] text-[#F7E6B4]"
                  : "gold-border text-[#F7E6B4]/70 hover:border-[rgba(230,180,85,0.4)]"
              }`}
              data-testid={`cash-source-${s.id}`}
            >
              <s.icon size={18} className={source === s.id ? "text-[#E6B455]" : ""} />
              <span className="text-xs font-semibold">{s.label}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-5">
          <span className="text-3xl font-bold text-[#E6B455]">{cur.symbol}</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-5xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/20 min-w-0"
            data-testid="cash-amount-input"
            autoFocus
          />
        </div>

        <QuickAmounts currency={currency} onPick={setAmount} selected={amount} />

        <input
          type="text"
          placeholder={source === "upi" ? "UPI ref / UTR number" : "Reference (optional)"}
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          className="w-full bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35"
          data-testid="cash-ref-input"
        />
      </div>

      <div className="mt-6">
        {done ? (
          <div className="btn-gold rounded-xl p-4 flex items-center justify-center gap-2" data-testid="cash-done">
            <Check size={18} /> Transaction logged!
          </div>
        ) : (
          <button
            onClick={submit}
            disabled={!valid}
            className="btn-gold w-full py-3.5 rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cash-submit-btn"
          >
            Record {formatMoney(amount || 0, currency)}
          </button>
        )}
      </div>
    </div>
  );
}
